import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, TextField, Button, Stack, CircularProgress, Divider } from '@mui/material';
import { Save, Schedule as ScheduleIcon, NightsStay as RamadanIcon, Timer as OvertimeIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../../contexts/LanguageContext';
import axios from 'axios';
import config from '../../../config';
import { toast } from 'react-toastify';

const HRSettingsTab: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [settings, setSettings] = useState({
    DailyWorkHours: '8',
    RamadanDailyWorkHours: '6',
    RamadanStartDate: '',
    RamadanEndDate: '',
    MaxOvertimeHoursPerDay: '10',
  });

  const t = {
    title: isRTL ? 'إعدادات الموظفين والدوام' : 'HR & Attendance Settings',
    workHours: isRTL ? 'ساعات العمل' : 'Work Hours',
    dailyWorkHours: isRTL ? 'ساعات العمل اليومية (أيام عادية)' : 'Daily Work Hours (Normal Days)',
    ramadan: isRTL ? 'إعدادات رمضان' : 'Ramadan Settings',
    ramadanHours: isRTL ? 'ساعات العمل في رمضان' : 'Ramadan Daily Work Hours',
    ramadanStart: isRTL ? 'أول يوم رمضان' : 'Ramadan Start Date',
    ramadanEnd: isRTL ? 'آخر يوم رمضان' : 'Ramadan End Date',
    overtime: isRTL ? 'الساعات الإضافية' : 'Overtime',
    maxOvertime: isRTL ? 'الحد الأقصى للساعات الإضافية يومياً' : 'Max Overtime Hours Per Day',
    save: isRTL ? 'حفظ الإعدادات' : 'Save Settings',
    saveSuccess: isRTL ? 'تم حفظ الإعدادات بنجاح' : 'Settings saved successfully',
    saveError: isRTL ? 'خطأ في حفظ الإعدادات' : 'Error saving settings',
    loadError: isRTL ? 'خطأ في تحميل الإعدادات' : 'Error loading settings',
    hint1: isRTL ? 'عدد الساعات التي يُملأ بها زر "تعبئة 8س" في التايم شيت' : 'Hours used by the "Fill 8h" button in TimeSheet',
    hint2: isRTL ? 'تواريخ رمضان تُحدّث سنوياً — الأشهر الهجرية تختلف كل سنة' : 'Ramadan dates are updated yearly — Hijri months shift each year',
    hint3: isRTL ? 'الحد الأقصى المسموح إدخاله كساعات إضافية في اليوم الواحد' : 'Maximum overtime hours allowed per day entry',
  };

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${config.API_BASE_URL}/SystemSettings/category/HR`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...data }));
    } catch { toast.error(t.loadError); }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const settingsToSave: Record<string, string> = {};
      Object.entries(settings).forEach(([key, value]) => { settingsToSave[key] = value; });
      await axios.put(`${config.API_BASE_URL}/SystemSettings`, settingsToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t.saveSuccess);
    } catch { toast.error(t.saveError); }
    setSaving(false);
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={3}>{t.title}</Typography>

      {/* Work Hours */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <ScheduleIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>{t.workHours}</Typography>
          </Stack>
          <TextField
            type="number"
            label={t.dailyWorkHours}
            value={settings.DailyWorkHours}
            onChange={e => setSettings({ ...settings, DailyWorkHours: e.target.value })}
            inputProps={{ min: 1, max: 12 }}
            size="small"
            sx={{ width: 300 }}
            helperText={t.hint1}
          />
        </CardContent>
      </Card>

      {/* Ramadan */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <RamadanIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>{t.ramadan}</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" mb={2} display="block">{t.hint2}</Typography>
          <Stack spacing={2}>
            <TextField
              type="number"
              label={t.ramadanHours}
              value={settings.RamadanDailyWorkHours}
              onChange={e => setSettings({ ...settings, RamadanDailyWorkHours: e.target.value })}
              inputProps={{ min: 1, max: 12 }}
              size="small"
              sx={{ width: 300 }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack direction="row" spacing={2}>
                <DatePicker
                  label={t.ramadanStart}
                  value={settings.RamadanStartDate ? new Date(settings.RamadanStartDate) : null}
                  onChange={date => setSettings({ ...settings, RamadanStartDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                  format="dd/MM/yyyy"
                  slotProps={{ textField: { size: 'small', sx: { width: 200 } } }}
                />
                <DatePicker
                  label={t.ramadanEnd}
                  value={settings.RamadanEndDate ? new Date(settings.RamadanEndDate) : null}
                  onChange={date => setSettings({ ...settings, RamadanEndDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                  format="dd/MM/yyyy"
                  slotProps={{ textField: { size: 'small', sx: { width: 200 } } }}
                />
              </Stack>
            </LocalizationProvider>
          </Stack>
        </CardContent>
      </Card>

      {/* Overtime */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <OvertimeIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>{t.overtime}</Typography>
          </Stack>
          <TextField
            type="number"
            label={t.maxOvertime}
            value={settings.MaxOvertimeHoursPerDay}
            onChange={e => setSettings({ ...settings, MaxOvertimeHoursPerDay: e.target.value })}
            inputProps={{ min: 0, max: 24 }}
            size="small"
            sx={{ width: 300 }}
            helperText={t.hint3}
          />
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />
      <Button variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <Save />} onClick={handleSave} disabled={saving} size="large">
        {t.save}
      </Button>
    </Box>
  );
};

export default HRSettingsTab;
